import React from "react";
import { MasterCalendarEntity } from "../../../../compliance/domain/entities/MasterCalendarEntity";
import { Table } from "../../../../../ui/components/Table";
import { Badge } from "../../../../../ui/components/Badge";

interface CalendarEntriesTableProps {
  entries: MasterCalendarEntity[];
}

// Helper function to get badge variant based on status
const getStatusBadgeVariant = (
  status?: string
): "success" | "error" | "warning" | "neutral" => {
  if (!status) return "neutral";

  switch (status.toLowerCase()) {
    case "approved":
    case "completed":
      return "success";
    case "overdue":
      return "error";
    case "under_review":
    case "pending":
      return "warning";
    default:
      return "neutral";
  }
};

// Helper function to format status text
const formatStatus = (status?: string): string => {
  if (!status) return "Pending";

  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const CalendarEntriesTable: React.FC<CalendarEntriesTableProps> = ({
  entries,
}) => {
  if (entries.length === 0) {
    return (
      <div className="flex w-full items-center justify-center p-8 text-body font-body text-subtext-color">
        No calendar entries found for this compliance type.
      </div>
    );
  }

  return (
    <Table
      header={
        <Table.HeaderRow>
          <Table.HeaderCell>Form Name</Table.HeaderCell>
          <Table.HeaderCell>Period</Table.HeaderCell>
          <Table.HeaderCell>Period Start</Table.HeaderCell>
          <Table.HeaderCell>Period End</Table.HeaderCell>
          <Table.HeaderCell>Due Date</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
        </Table.HeaderRow>
      }
    >
      {entries.map((entry) => (
        <Table.Row key={entry.id}>
          <Table.Cell>
            <span className="text-body-bold font-body-bold text-default-font">
              {entry.compliance_form_name}
            </span>
          </Table.Cell>
          <Table.Cell>
            <span className="text-body font-body text-neutral-500">
              {entry.period}
            </span>
          </Table.Cell>
          <Table.Cell>
            <span className="text-body font-body text-neutral-500">
              {new Date(entry.period_start_date).toLocaleDateString()}
            </span>
          </Table.Cell>
          <Table.Cell>
            <span className="text-body font-body text-neutral-500">
              {new Date(entry.period_end_date).toLocaleDateString()}
            </span>
          </Table.Cell>
          <Table.Cell>
            <span className="text-body font-body text-neutral-500">
              {new Date(entry.due_date).toLocaleDateString()}
            </span>
          </Table.Cell>
          <Table.Cell>
            <Badge variant={getStatusBadgeVariant(entry.status)}>
              {formatStatus(entry.status)}
            </Badge>
          </Table.Cell>
        </Table.Row>
      ))}
    </Table>
  );
};

